<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-4">
        <select class="form-select" v-model="actionFilter">
          <option value="">Todos los tipos</option>
          <option value="COMPROBANTE">Comprobante</option>
          <option value="CERTIFICADO">Certificado</option>
          <option value="CONTRASEÑA">Restablecer Contraseña</option>
        </select>
      </div>
      <div class="col-md-8">
        <input
          type="text"
          class="form-control"
          v-model="searchText"
          placeholder="Buscar por documento..."
        />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th class="text-center">Nro</th>
                <th class="text-center">Tipo</th>
                <th class="text-center">Creado por</th>
                <th class="text-center">Documento</th>
                <th class="text-center">Fecha</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center">
                  <i class="fa fa-spinner fa-spin"></i> Cargando...
                </td>
              </tr>
              <tr v-else-if="paginatedItems.length === 0">
                <td colspan="7" class="text-center">
                  <EmptyState
                    title="No hay tickets activos."
                    :size="450"
                    fontSize="fs-5"
                  />
                </td>
              </tr>
              <tr
                v-else
                v-for="(ticket, index) in paginatedItems"
                :key="ticket.id"
              >
                <td class="text-center">
                  {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                </td>
                <td class="text-center">
                  <span class="badge" :class="actionBadgeClass(ticket.action)">
                    {{ actionText(ticket.action) }}
                  </span>
                </td>
                <td class="text-center">{{ ticket.user?.name || "N/A" }}</td>
                <td class="text-center">{{ ticket.participant_document }}</td>
                <td class="text-center">{{ formatDate(ticket.created_at) }}</td>
                <td class="text-center">
                  <div class="btn-group" role="group">
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click="viewDetail(ticket)"
                      title="Ver detalle"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                    <button
                      v-if="canResolve(ticket)"
                      type="button"
                      class="btn btn-sm btn-success"
                      @click="resolveTicket(ticket)"
                      title="Marcar como resuelto"
                    >
                      <i class="fa fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Pagination
          v-if="filteredList.length > itemsPerPage"
          :current-page="currentPage"
          :total-items="filteredList.length"
          :items-per-page="itemsPerPage"
          @page-changed="handlePageChange"
        />
      </div>
    </div>

    <DetailModal ref="detailModal" @ticket-updated="onTicketUpdated" />
  </div>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import axios from "@/includes/connection";
import Pagination from "@/pages/components/Pagination.vue";
import DetailModal from "./DetailModal.vue";
import EmptyState from "@/components/emptyState.vue";
import { toast } from "vue3-toastify";

const props = defineProps({
  tickets: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["ticket-updated"]);
const swal = inject("$swal");

const detailModal = ref(null);
const actionFilter = ref("");
const searchText = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);

const filteredList = computed(() => {
  let filtered = [...props.tickets];

  if (actionFilter.value) {
    filtered = filtered.filter(
      (ticket) => ticket.action === actionFilter.value
    );
  }

  if (searchText.value) {
    const search = searchText.value.toLowerCase();
    filtered = filtered.filter(
      (ticket) =>
        ticket.participant_document.toLowerCase().includes(search) ||
        ticket.event?.title.toLowerCase().includes(search) ||
        ticket.user?.name.toLowerCase().includes(search)
    );
  }

  return filtered;
});

const paginatedItems = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return filteredList.value.slice(startIndex, endIndex);
});

watch([actionFilter, searchText], () => {
  currentPage.value = 1;
});

function handlePageChange(page) {
  currentPage.value = page;
}

function viewDetail(ticket) {
  detailModal.value.show(ticket);
}

function onTicketUpdated(updatedTicket) {
  emit("ticket-updated", updatedTicket);
}

function canResolve(ticket) {
  // Solo usuarios con permisos pueden resolver (ejemplo: admin o soporte)
  const currentUser = getCurrentUser();
  return (
    currentUser &&
    (currentUser.role === "ADMIN" || currentUser.role === "SUPPORT")
  );
}

function getCurrentUser() {
  try {
    const user = JSON.parse(localStorage.getItem("User"));
    return user;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
}

async function resolveTicket(ticket) {
  const result = await swal.fire({
    title: "¿Marcar como resuelto?",
    text: "El ticket será marcado como RESUELTO",
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Sí, resolver",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return;

  try {
    const response = await axios.patch(`/ticket/${ticket.id}/status`, {
      status: "RESUELTO",
    });

    if (response.data) {
      emit("ticket-updated", response.data);
      toast.success("Ticket marcado como resuelto");
    }
  } catch (error) {
    console.error("Error", error);
    toast.error("Error al resolver el ticket");
  }
}

function actionText(action) {
  const texts = {
    COMPROBANTE: "Comprobante",
    CERTIFICADO: "Certificado",
    CONTRASEÑA: "Contraseña",
  };
  return texts[action] || action;
}

function actionBadgeClass(action) {
  const classes = {
    COMPROBANTE: "bg-primary",
    CERTIFICADO: "bg-success",
    CONTRASEÑA: "bg-warning",
  };
  return classes[action] || "bg-secondary";
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
</script>
